import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import BreadcrumbNav from '../../brastrap/components/common/breadcrumb-nav/BreadcrumbNav.jsx';
import BreadcrumbSchema from '../../brastrap/components/common/breadcrumb-schema/BreadcrumbSchema.jsx';
import {
  LOCALE_PREFIXES,
  URL_NORMALISATION_RULES,
  LABEL_NORMALISATION_RULES,
  CAPITALISE_WORD_BOUNDARIES,
  COLLECTIONS_VARIABLE,
  PRODUCTS_VARIABLE,
  SHOP_BY_VARIABLE,
  SIZE_VARIABLE,
  BREADCRUMB_SEGMENTS_TO_EXCLUDE,
  SHOP_BY_SIZE_LABELS,
} from '../../config/breadcrumbConfig.js';

export const isWindowAvailable = typeof window !== 'undefined';

const extractSize = url => {
  if (!url) return null;
  const match = url.match(/\/shop-by\/size\/(\d+)([a-z]+)-/i);
  return match || null;
};

const getGenericParentLabelsFromMegaMenu = megaMenu => {
  if (!Array.isArray(megaMenu)) return new Set();

  return new Set(
    megaMenu
      .filter(
        item =>
          item.type === 'mega-menu' ||
          (item.type === 'link' &&
            item.url?.startsWith(`/${COLLECTIONS_VARIABLE}/`))
      )
      .map(item => item.label?.trim())
      .filter(Boolean)
  );
};

const cleanTrail = trail => {
  if (!Array.isArray(trail)) return [];

  return trail
    .filter(item => !BREADCRUMB_SEGMENTS_TO_EXCLUDE.has(item.segment))
    .map(item => {
      if (SHOP_BY_SIZE_LABELS.has(item.label)) {
        const lastShopBySize = isWindowAvailable
          ? sessionStorage.getItem('lastShopBySize')
          : null;

        const sizeLabel = extractSize(lastShopBySize);

        if (sizeLabel) {
          return {
            ...item,
            label: `Shop By Size ${sizeLabel[1]}${sizeLabel[2]}`,
            url: lastShopBySize,
          };
        }
      }

      const cleanedLabel = item.label.replace(/\b[a-z]*\d+[a-z]*$/i, '').trim();
      return {
        ...item,
        label: cleanedLabel,
      };
    });
};

const getDynamicBreadcrumbOverrides = (pathname = '', collection = '') => {
  if (!isWindowAvailable) return {};

  const segments = pathname.split('/').filter(Boolean);
  const isProductPage = segments[0] === PRODUCTS_VARIABLE && segments[1];

  if (!isProductPage || !collection) {
    return {};
  }

  return {
    products: {
      label: collection,
      url: `/collections/${collection}`,
    },
  };
};

const buildMegaMenuSlugLookup = megaMenu => {
  const lookup = new Map();

  if (!Array.isArray(megaMenu)) return lookup;

  megaMenu.forEach(section => {
    if (section.reference) {
      lookup.set(section.reference.replace(/-$/, ''), {
        label: section.label,
        url: section.url,
      });
    }

    (section.columns || []).forEach(column => {
      (column.linkGroups || []).forEach(group => {
        (group.links || []).forEach(link => {
          if (!link.url) return;

          const match = link.url.match(/\/collections\/([^/]+)\//);
          if (match) {
            lookup.set(match[1], {
              label: section.label,
              url: link.url,
            });
          }
        });
      });
    });
  });

  return lookup;
};

const generateBreadcrumbTrail = (
  pathname = '',
  megaMenu = [],
  collection = ''
) => {
  if (!isWindowAvailable) return [];

  const megaMenuLookup = buildMegaMenuSlugLookup(megaMenu);

  const genericParentLabels = getGenericParentLabelsFromMegaMenu(megaMenu);
  const stripLocale = value =>
    LOCALE_PREFIXES.reduce((acc, regex) => acc.replace(regex, ''), value);

  const normaliseUrl = url =>
    URL_NORMALISATION_RULES.reduce(
      (acc, { regex, replace }) => acc.replace(regex, replace),
      stripLocale(url)
    );

  const normaliseLabel = label => {
    let value = stripLocale(label);

    LABEL_NORMALISATION_RULES.forEach(({ regex, replace }) => {
      value = value.replace(regex, replace);
    });

    if (CAPITALISE_WORD_BOUNDARIES) {
      value = value.replace(/\b\w/g, char => char.toUpperCase());
    }

    return value.trim();
  };

  const segments = pathname.split('/').filter(Boolean);
  const overrides = getDynamicBreadcrumbOverrides(pathname, collection);

  const isProductPage = segments[0] === PRODUCTS_VARIABLE && segments[1];
  const isRealCollection =
    Boolean(collection) && collection !== PRODUCTS_VARIABLE;

  const trail = segments.map((segment, index) => {
    const override = overrides[segment];
    const rawUrl = `/${segments.slice(0, index + 1).join('/')}`;
    const isProductsSegment = segment === PRODUCTS_VARIABLE;
    const cleanedUrl = normaliseUrl(stripLocale(rawUrl));

    const breadcrumb = megaMenuLookup.get(segment) || {
      label: segment,
      url: `/${COLLECTIONS_VARIABLE}/${segment}`,
    };

    const isLast = index === segments.length - 1;
    const segmentLabel = normaliseLabel(decodeURIComponent(segment));

    const rawLabel =
      override?.label ||
      (isLast && genericParentLabels.has(breadcrumb.label)
        ? segmentLabel
        : breadcrumb.label) ||
      segmentLabel;

    const isDisabled = isProductPage && isProductsSegment && !isRealCollection;

    return {
      segment,
      label: normaliseLabel(rawLabel),
      url: override?.url || breadcrumb.url || cleanedUrl,
      last: isLast,
      isDisabled,
    };
  });

  return [{ label: 'Home', url: '/', last: false }, ...trail];
};

const checkWebsiteLocation = pathname => {
  const segments = pathname.split('/').filter(Boolean);

  const isProductPage = segments[0] === PRODUCTS_VARIABLE && segments[1];
  const isCollectionPage = segments[0] === COLLECTIONS_VARIABLE && segments[1];
  const isShopBySize =
    segments[0] === SHOP_BY_VARIABLE && segments[1] === SIZE_VARIABLE;

  return isProductPage || isCollectionPage || isShopBySize;
};

const isInvalid = v =>
  v == null || v === '' || v === 'undefined' || v === 'null';

export const BreadcrumbContainer = ({ pathname, megaMenu, collection }) => {
  const stored = isWindowAvailable
    ? sessionStorage.getItem('plpCollection')
    : null;

  const safeStored = isInvalid(stored) ? undefined : stored;
  const safeReduxCollection = isInvalid(collection) ? undefined : collection;

  const resolvedCollection =
    safeStored || safeReduxCollection || PRODUCTS_VARIABLE;

  const rawTrail = generateBreadcrumbTrail(
    pathname,
    megaMenu,
    resolvedCollection
  );

  const trail = cleanTrail(rawTrail);

  useEffect(() => {
    if (!isWindowAvailable) return;

    const currentUrl = window.location.pathname + window.location.search;
    const previousUrl = sessionStorage.getItem('previousPageUrl');

    sessionStorage.setItem('previousPageUrl', currentUrl);

    if (previousUrl?.includes('/shop-by/size')) {
      sessionStorage.setItem('lastShopBySize', previousUrl);
    }
  }, [pathname]);

  return (
    <div>
      {checkWebsiteLocation(pathname) && (
        <div className="c-masthead__navigation-breadcrumb-container">
          <BreadcrumbSchema trail={trail} />
          <BreadcrumbNav trail={trail} />
        </div>
      )}
    </div>
  );
};

BreadcrumbContainer.propTypes = {
  pathname: PropTypes.string,
  megaMenu: PropTypes.array,
  collection: PropTypes.string,
};

const mapStateToProps = ({
  app: {
    config: { basePath, currency },
    routing,
  },
  masthead: { megaMenu },
  productListing: { collection },
}) => {
  const pathname = routing.pathname.replace(basePath, '');
  return {
    basePath,
    currency,
    routing,
    pathname,
    collection,
    megaMenu,
  };
};

export default connect(mapStateToProps)(BreadcrumbContainer);
